import React, { useState } from 'react';
import Button from '../ui/Button';
import styles from './ReportProblem.module.css';

const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:4000';

const CATEGORIES = [
  { value: 'damage', label: 'Box damaged' },
  { value: 'missing_box', label: 'Box missing' },
  { value: 'delay', label: 'Late' },
  { value: 'wrong_delivery', label: 'Wrong delivery' },
  { value: 'other', label: 'Other' },
];

/**
 * "Problem with this shipment?" under the tracking result. Lands in Sahari's
 * Complaints (Source = Website). The phone must be the one on the booking —
 * the server checks it, so no one can file against someone else's AWB.
 */
const ReportProblem: React.FC<{ awb: string | null }> = ({ awb }) => {
  const [open, setOpen] = useState(false);
  const [awbNumber, setAwbNumber] = useState(awb ?? '');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [category, setCategory] = useState('damage');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`${BASE}/api/v1/complaints/public/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          awbNumber: awbNumber.trim(),
          name: name.trim(),
          phone: phone.trim(),
          category,
          message: message.trim() || undefined,
        }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = body?.errors?.[0]?.message ?? body?.message;
        throw new Error(msg || 'Could not send. Please try again.');
      }
      setDone(body?.data?.complaintNumber ?? '');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  };

  if (done !== null) {
    return (
      <div className={styles.card} role="status">
        <h2 className={styles.title}>We got it</h2>
        <p className={styles.text}>
          Your complaint number is <b>{done}</b>. Our team will call you soon.
        </p>
      </div>
    );
  }

  if (!open) {
    return (
      <div className={styles.bar}>
        <span className={styles.text}>Problem with this shipment?</span>
        <Button variant="outline" onClick={() => setOpen(true)}>
          Report a problem
        </Button>
      </div>
    );
  }

  return (
    <form className={styles.card} onSubmit={send}>
      <h2 className={styles.title}>Report a problem</h2>
      <p className={styles.text}>Tell us here — our team will call you.</p>
      <div className={styles.grid}>
        <label className={styles.field}>
          AWB number
          <input value={awbNumber} onChange={(e) => setAwbNumber(e.target.value)} required minLength={3} />
        </label>
        <label className={styles.field}>
          Your name
          <input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} autoComplete="name" />
        </label>
        <label className={styles.field}>
          Phone (the one on the booking)
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            minLength={7}
            inputMode="tel"
            autoComplete="tel"
          />
        </label>
        <label className={styles.field}>
          What is the problem?
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className={styles.field}>
        Tell us more
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={2000} />
      </label>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.actions}>
        <Button variant="ghost" type="button" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={sending}>
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </form>
  );
};

export default ReportProblem;
